import { useState } from "react";
import { api } from "@/lib/api";
import { FilePdf, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Downloads a PDF from the backend and triggers a browser save-as.
 * kind: 'property' | 'auction' | 'zone'
 * For 'zone', pass id as `${cityId}/${zoneName}`.
 */
export default function PdfButton({ kind, id, size = "sm", label = "PDF", className = "" }) {
  const [busy, setBusy] = useState(false);

  const onClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setBusy(true);
    try {
      const resp = await api.get(`/reports/${kind}/${id}`, { responseType: "blob" });
      const filename = extractFilename(resp.headers?.["content-disposition"]) || `immoscope-${kind}.pdf`;
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF scaricato");
    } catch {
      toast.error("Impossibile generare il PDF");
    } finally {
      setBusy(false);
    }
  };

  const iconSize = size === "sm" ? 14 : 16;
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 px-2 h-8 border font-mono-data text-[11px] transition-colors bg-transparent text-zinc-400 border-white/10 hover:bg-white/5 hover:text-cyan-400 disabled:opacity-60 ${className}`}
      data-testid={`pdf-btn-${kind}-${typeof id === "string" ? id.replace(/\//g, "-") : id}`}
      aria-label="Scarica PDF"
      title="Scarica report PDF"
    >
      {busy ? <CircleNotch size={iconSize} weight="bold" className="animate-spin" /> : <FilePdf size={iconSize} weight="bold" />}
      <span>{busy ? "..." : label}</span>
    </button>
  );
}

function extractFilename(disposition) {
  if (!disposition) return null;
  const m = /filename="?([^"]+)"?/i.exec(disposition);
  return m ? m[1] : null;
}
